import http from "../../utils/http-common";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";


export function useTagTypes() {
  return useQuery({
    queryKey: ["tag-types"],
    queryFn: async () => {
      const { data } = await http.get("/tags/types");
      return data;
    },
  });
}

export function useTagType(typeId) {
  return useQuery({
    queryKey: ["tag-type", typeId],
    queryFn: async () => {
      if (!typeId) return null;
      const { data } = await http.get(`/tags/types/${typeId}`);
      return data;
    },
    enabled: !!typeId,
  });
}

export function useCreateTagType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, parent }) => {
      const { data } = await http.post("/tags/types", { name, parent });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["tag-types"]),
  });
}


export function useUpdateTagType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name, parent }) => {
      const { data } = await http.put(`/tags/types/${id}`, { name, parent });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["tag-types"]),
  });
}
export function useUpdateTagTree() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, tree }) => {
      await http.put(`/tags/types/${id}/tree`, { tree });
    },
    onSuccess: () => queryClient.invalidateQueries(["tag-types"]),
  });
}


export function useTagValues() {
  return useQuery({
    queryKey: ["tag-values"],
    queryFn: async () => {
      const { data } = await http.get("/tags/values");
      return data;
    },
  });
}

export function useTagValue(valueId) {
  return useQuery({
    queryKey: ["tag-value", valueId],
    queryFn: async () => {
      if (!valueId) return null;
      const { data } = await http.get(`/tags/values/${valueId}`);
      return data;
    },
    enabled: !!valueId,
  });
}

export function useCreateTagValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, name, previous }) => {
      const { data } = await http.post("/tags/values", { type, name, previous });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["tag-values"]),
  });
}

export function useUpdateTagValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, type, name, previous }) => {
      const { data } = await http.put(`/tags/values/${id}`, { type, name, previous });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["tag-values"]),
  });
}


export function useCaseTags(caseId) {
  return useQuery({
    queryKey: ["case-tags", caseId],
    queryFn: async () => {
      if (!caseId) return [];
      const { data } = await http.get(`/cases/${caseId}/tags`);
      return data || [];
    },
    enabled: !!caseId,
  });
}


export function useProductCatalog() {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data } = await http.get("/tags/types");
      return Array.isArray(data) ? data : [data];
    },
  });
}

export function useDeleteTagType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await http.delete(`/tags/types/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries(["tag-types"]),
  });
}
export function useTagValuesByType(typeId) {
  return useQuery({
    queryKey: ["tag-values", typeId],
    queryFn: async () => {
      if (!typeId) return [];
      const { data } = await http.get(`/tags/values?type=${typeId}`);
      return data;
    },
    enabled: !!typeId,
  });
}



export function useDeleteTagValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await http.delete(`/tags/values/${id}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["tag-values"]);
    },
  });
}


export function useAddCaseTag(caseId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tag) => {
      const { data } = await http.post(`/cases/${caseId}/tags`, tag);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["case-tags", caseId]),
  });
}

export function useRemoveCaseTag(caseId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tagId) => {
      await http.delete(`/cases/${caseId}/tags/${tagId}`);
    },
    onSuccess: () => queryClient.invalidateQueries(["case-tags", caseId]),
  });
}

export function useSuiteTags(suiteId) {
  return useQuery({
    queryKey: ["suite-tags", suiteId],
    queryFn: async () => {
      if (!suiteId) return [];
      const { data } = await http.get(`/suites/${suiteId}/tags`);
      return data || [];
    },
    enabled: !!suiteId,
  });
}

export function useAddSuiteTag(suiteId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tag) => {
      const { data } = await http.post(`/suites/${suiteId}/tags`, tag);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries(["suite-tags", suiteId]),
  });
}

export function useRemoveSuiteTag(suiteId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tagId) => {
      await http.delete(`/suites/${suiteId}/tags/${tagId}`);
    },
    onSuccess: () => queryClient.invalidateQueries(["suite-tags", suiteId]),
  });
}
export function useCasesByTags(tagValueIds, options = {}) {
  return useQuery({
    queryKey: ["cases-by-tags", tagValueIds],
    queryFn: async () => {
      if (!tagValueIds || tagValueIds.length === 0) return [];
      const ids = tagValueIds.join(",");
      const { data } = await http.get(`/cases/by_tags?tag_value_ids=${ids}`);
      return data;
    },
    enabled: !!tagValueIds && tagValueIds.length > 0,
    ...options,
  });
}
export function useCasesByTagValues(tagValueIds) {
  return useQuery({
    queryKey: ["cases-by-tags", tagValueIds],
    queryFn: async () => {
      if (!tagValueIds?.length) return [];
      const ids = tagValueIds.join(",");
      const { data } = await http.get(`/cases/by-tags?tag_value_ids=${ids}`);
      return data || [];
    },
    enabled: !!tagValueIds?.length,
  });
}
